import React from "react";

import { makeStyles } from "tss-react/mui";
import { Typography, Grid, Skeleton } from "@mui/material";

import { OperationStatus } from "libLegacy/api/models";

import { format, isValid } from "date-fns";

const useStyles = makeStyles()((theme) => ({
  weatherIcon: {
    height: "25px",
  },
}));

const OPERATION_OPTIONS: OperationOptions = {
  UNSPECIFIED_STATUS: {
    color: "error",
    messageTitle: "Problema na geração.",
    messageBody: "Se persistir, entre em contato com o seu integrador",
  },
  NORMAL: {
    color: "primary",
    messageTitle: "Operação normal",
  },
  NO_COMMUNICATION: {
    color: "secondary",
    messageTitle: "Sem comunicação.",
  },
};

export const LocalSystemStatus = ({
  temperature,
  status,
  iconId,
  iconAlt,
  lastRead,
  isLoading = false,
}: LocalSystemStatusProps) => {
  const { classes } = useStyles();
  const { messageTitle, messageBody, color } =
    OPERATION_OPTIONS[status || "UNSPECIFIED_STATUS"];

  const formattedTemperature = temperature
    ? ` ${Math.round(temperature)}ºC`
    : null;

  function formatDateNumberToString(dateMilisseconds: number) {
    const parsedDate = new Date(dateMilisseconds);

    if (!isValid(parsedDate)) {
      return "";
    }

    const formattedDate = format(parsedDate, "dd/MM/yy - H:mm");
    return formattedDate;
  }

  return (
    <Grid container item direction="row">
      <Grid
        item
        container
        justifyContent="center"
        alignItems="flex-end"
        sx={{ flex: 1.3 }}
      >
        <img src="/static/img/solutions/solar-system.png" alt="" width="100%" />
      </Grid>
      <Grid
        container
        item
        sx={{ flex: 1 }}
        direction="column"
        justifyContent="center"
        alignItems="center"
      >
        <Typography
          align="center"
          sx={{ width: "100%" }}
          variant="subtitle1"
          fontWeight="normal"
          color="text.primary"
        >
          {!isLoading && iconId && (
            <img
              className={classes.weatherIcon}
              src={`https://openweathermap.org/img/wn/${iconId}@2x.png`}
              alt={iconAlt || ""}
            />
          )}
          {isLoading ? <Skeleton width="60%" /> : formattedTemperature}
        </Typography>
        <Grid
          item
          container
          direction="column"
          alignItems="center"
          justifyContent="center"
        >
          <Typography
            align="center"
            sx={{ width: "100%" }}
            variant="body1"
            fontWeight="medium"
            color={color}
          >
            {isLoading || !status ? <Skeleton /> : messageTitle}
          </Typography>
          <Typography
            align="center"
            variant="caption"
            color="text.primary"
            sx={{ width: "100%" }}
          >
            {isLoading || !status ? (
              <Skeleton width="90%" />
            ) : (
              formatDateNumberToString(lastRead)
            )}
          </Typography>
          {!(isLoading && status && messageBody) && (
            <Typography variant="caption" color={color}>
              {messageBody}
            </Typography>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export type LocalSystemStatusProps = {
  status: OperationStatus | undefined;
  temperature: number | undefined;
  iconId: string | undefined;
  iconAlt: string | undefined;
  isLoading?: Boolean;
  lastRead: number;
};

type MuiTypographyColors =
  | "error"
  | "inherit"
  | "initial"
  | "primary"
  | "secondary"
  | "textPrimary"
  | "textSecondary";

type OperationOptions = Record<
  OperationStatus,
  { color: MuiTypographyColors; messageTitle: string; messageBody?: string }
>;
